import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FacebookIcon,
  WhatshappIcon,
  YoutubeIcon,
} from "../icons";
import {
  Instagram,
  Twitter,
  MapPin,
  Clock,
  ShieldCheck,
  Settings,
  ChevronRight
} from "lucide-react";
import logoFooter from "../assets/logo/logo_footer.png";
import { useNavigation } from "../context/NavigationContext";

const serviceableLocations = [
  "Belda", "Temathani", "Medinipur"
];

const companyLinks = [
  { name: "FAQs", href: "/faqs" },
  { name: "Reviews", href: "/reviews" },
  { name: "Warranty Claim", href: "/warranty-claim" },
  { name: "Warranty Policy", href: "/warranty-policy" },
  { name: "Terms & Conditions", href: "/terms" },
  { name: "Privacy Policy", href: "/privacy-policy" },
  { name: "Franchise", href: "/franchise" },
  { name: 'Contact Us', href: '/contect-us' },
  { name: "About Us", href: "/about" },
];

const repairServices = [
  { name: "Smart Phone Repair", href: "/device/3" },
  { name: "Feature Phone Repair", href: "/device/4" },
  { name: "Tablet Repair", href: "/device/5" },
  { name: "Smart Watch Repair", href: "/device/6" },

];

const temperedGlass = [
  { name: "Mobile Tempered Glass", href: "/tempered/mobile" },
  { name: "iPad Tempered Glass", href: "/tempered/ipad" },
  { name: "Smart Watch Tempered Glass", href: "/tempered/watch" },
];

const FooterLinkColumn: React.FC<{
  title: string;
  links: { name: string; href: string }[] | string[];
}> = ({ title, links }) => {
  const navigate = useNavigate();
  const { setActiveNav } = useNavigation();

  const handleNavigation = (link: { name: string; href: string } | string) => {
    const name = typeof link === 'string' ? link : link.name;
    const href = typeof link === 'string' ? `/location/${link.toLowerCase().replace(' ', '-')}` : link.href;

    setActiveNav(name);
    navigate(href);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="flex flex-col space-y-4">
      <h3 className="text-base font-bold text-gray-800 uppercase tracking-wider mb-2">
        {title}
      </h3>
      <ul className="space-y-2.5">
        {links.map((link, idx) => (
          <li key={idx}>
            <button
              onClick={() => handleNavigation(link)}
              className="text-gray-600 text-sm hover:text-[#2563eb] transition-colors duration-200 text-left flex items-center group"
            >
              <ChevronRight className="w-3 h-3 mr-1 opacity-0 group-hover:opacity-100 transition-opacity text-[#2563eb]" />
              {typeof link === 'string' ? link : link.name}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const AppFooter: React.FC = () => {
  const navigate = useNavigate();

  return (
    <footer className="bg-white pt-0">
      {/* 1. TOP CONTACT BAR */}
      <div className="bg-[#2563eb] py-4 text-white">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-wrap justify-center md:justify-start items-center gap-4 text-base font-medium">
            <span className="flex items-center gap-2">
              Have more questions? Reach out to us at
              <a href="tel: 8695864141" className=" font-bold border-b-2 border-transparent hover:border-white transition-all">8695864141</a>
            </span>
            <span className="hidden md:inline text-white/40">|</span>
            <a
              href="mailto:info@phonelab.co.in"
              className="font-bold border-b-2 border-transparent hover:border-white transition-all"
            >
              info@phonelab.co.in
            </a>
          </div>
          <button
            onClick={() => navigate('/repair')}
            className="bg-white text-[#2563eb] px-8 py-2 rounded-full text-base font-bold shadow-md hover:bg-gray-100 transition-all transform hover:scale-105 active:scale-95"
          >
            Repair My Device
          </button>
        </div>
      </div>

      {/* 3. MAIN FOOTER CONTENT */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-12">
          {/* Brand Info */}
          <div className="col-span-2 lg:col-span-1 flex flex-col space-y-8">
            <div className="bg-gray-900 p-4 rounded-xl inline-block w-fit">
              <img
                src={logoFooter}
                alt="PhoneLab Logo"
                className="w-40 object-contain"
              />
            </div>
            <div className="flex flex-col space-y-5">
              <h4 className="text-sm font-bold text-gray-800 uppercase tracking-widest border-l-4 border-[#2563eb] pl-3">Follow us on</h4>
              <div className="flex gap-3">
                {[
                  { icon: <Twitter className="w-5 h-5" />, href: "#" },
                  { icon: <FacebookIcon className="w-5 h-5 brand-fill" />, href: "#" },
                  { icon: <Instagram className="w-5 h-5" />, href: "#" },
                  { icon: <YoutubeIcon className="w-5 h-5 brand-fill" />, href: "#" }
                ].map((social, i) => (
                  <a key={i} href={social.href} className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#2563eb] hover:text-white hover:border-[#2563eb] transition-all shadow-sm">
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Links */}
          <FooterLinkColumn title="Serviceabl e Locations" links={serviceableLocations} />
          <FooterLinkColumn title="Company" links={companyLinks} />
          <FooterLinkColumn title="Repair Services" links={repairServices} />
          <FooterLinkColumn title="Tempered Glass" links={temperedGlass} />
        </div>
      </div>

      {/* 4. BOTTOM BAR */}
      <div className="border-t border-gray-100 py-12 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 text-sm text-gray-600 leading-relaxed">
            <div className="space-y-6">
              <h5 className="text-base font-bold text-gray-800 uppercase tracking-widest border-b-2 border-gray-200 pb-2 inline-block">Office Addresses</h5>
              <div className="flex flex-col space-y-5">
                <div className="flex gap-3">
                  <MapPin className="w-5 h-5 text-[#2563eb] shrink-0" />
                  <p>
                    <strong className="text-gray-800 block text-base mb-1">West Bengal Office</strong>
                    Lutunia, Sabang,Paschim Medinipur West Bengal-721166
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <h5 className="text-base font-bold text-gray-800 uppercase tracking-widest border-b-2 border-gray-200 pb-2 inline-block">Disclaimer</h5>
              <p className="text-gray-500">
                All product names, logos, and brands are the property of their respective owners. Names/logos used on this website are for identification only and do not imply endorsement. PhoneLab is an independent third-party repair service provider and is not affiliated with any brand mentioned on this website.
              </p>
              <div className="mt-8 pt-8 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4">
                <p className="font-semibold text-gray-700">
                  © Copyright 2025. Design by PhoneLab
                </p>
                <div className="flex gap-6 text-xs font-bold uppercase tracking-widest text-gray-400">
                  <a href="#" className="hover:text-[#2563eb]">Sitemap</a>
                  <a href="#" className="hover:text-[#2563eb]">Cookie Policy</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default AppFooter;
